import { useMemo } from "react";

import Highcharts, { Point, SankeyNodeObject, SeriesSankeyPointOptionsObject } from "highcharts";
import { useIntl } from "react-intl";

import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";

import localSettings from "@app/config/LocalSettings";

interface CustomData {
  adjustedValue: number;
  allTimeDeals: number;
  currentDeals: number;
  dealsPercent: number;
  stage: Stage;
  totalValue: number;
  weightPercent: number;
}

const stagesToSankeySeriesData = (
  stageSummaries: StageSummary[]
): Highcharts.SeriesSankeyPointOptionsObject[] =>
  stageSummaries.reduce<Highcharts.SeriesSankeyPointOptionsObject[]>(
    (result, stageSummary, i, stageSummaries) =>
      i < stageSummaries.length - 1
        ? [
            ...result,
            {
              custom: {
                adjustedValue:
                  (stageSummary.totalValue +
                    stageSummaries.slice(i + 1).reduce((s, { totalValue }) => s + totalValue, 0)) *
                  stageSummary.stage.weight,
                allTimeDeals:
                  stageSummary.totalDeals +
                  stageSummaries.slice(i + 1).reduce((s, { totalDeals }) => s + totalDeals, 0),
                currentDeals: stageSummary.totalDeals,
                dealsPercent:
                  (stageSummaries.slice(i).reduce((s, { totalDeals }) => s + totalDeals, 0) -
                    stageSummary.totalDeals) /
                  (stageSummary.totalDeals +
                    stageSummaries.slice(i).reduce((s, { totalDeals }) => s + totalDeals, 0)),
                stage: stageSummary.stage,
                totalValue:
                  stageSummary.totalValue +
                  stageSummaries.slice(i + 1).reduce((s, { totalValue }) => s + totalValue, 0),
                weightPercent: stageSummary.stage.weight,
              },
              from: stageSummary.stage.name,
              to: stageSummaries[i + 1].stage.name,
              weight:
                stageSummary.totalDeals +
                stageSummaries.slice(i + 1).reduce((s, { totalDeals }) => s + totalDeals, 0),
            },
          ]
        : [
            ...result,
            {
              custom: {
                adjustedValue: stageSummary.totalValue * stageSummary.stage.weight,
                allTimeDeals: stageSummary.totalDeals,
                currentDeals: stageSummary.totalDeals,
                dealsPercent:
                  (stageSummaries.slice(i).reduce((s, { totalDeals }) => s + totalDeals, 0) -
                    stageSummary.totalDeals) /
                  (stageSummary.totalDeals +
                    stageSummaries.slice(i).reduce((s, { totalDeals }) => s + totalDeals, 0)),
                stage: stageSummary.stage,
                totalValue: stageSummary.totalValue,
                weightPercent: stageSummary.stage.weight,
              },
              from: stageSummary.stage.name,
              weight: stageSummary.totalDeals,
            },
          ],
    []
  );

export const useChartConfig = (
  funnelSummary: StageSummary[],
  title: string,
  subtitle: string,
  onSelectStage: (stage: Stage) => void
): Highcharts.Options | undefined => {
  const intl = useIntl();
  return useMemo<Highcharts.Options | undefined>(() => {
    if (!funnelSummary.length) {
      return undefined;
    }

    return {
      accessibility: {
        point: {
          valueDescriptionFormat: "{index}. {point.from} to {point.to}, {point.weight}.",
        },
      },
      chart: {
        plotShadow: false,
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          cursor: "pointer",
          data: stagesToSankeySeriesData(funnelSummary),
          events: {
            click: function (event) {
              const stage: Stage =
                event.point.options.custom?.stage ?? // click on a line
                (event.point as any).linksFrom?.[0]?.custom?.stage ?? // click on a milestone before line
                funnelSummary[funnelSummary.length - 1].stage; // click on the last milestone
              onSelectStage(stage);
            },
          },
          tooltip: {
            headerFormat: "",
            nodeFormatter: function (this: SankeyNodeObject) {
              const nodeData: CustomData = (
                this.series.data as SeriesSankeyPointOptionsObject[]
              ).find((d) => d.from === this.id)?.custom as CustomData;

              return `<span><b>${this.name}</b></span><br/>
              <table style="margin-top: 8px;">
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.currentDeals",
                    defaultMessage: "Current # deals",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${nodeData?.currentDeals}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.allTimeDeals",
                    defaultMessage: "All-time # deals",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${nodeData?.allTimeDeals}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.percentDealsMadeStage",
                    defaultMessage: "% deals made stage",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(nodeData?.dealsPercent, {
                    maximumFractionDigits: 0,
                    style: "percent",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.totalValue",
                    defaultMessage: "Total value",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(nodeData?.totalValue, {
                    currency: localSettings.getCurrencyCode(),
                    minimumFractionDigits: 0,
                    style: "currency",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.weightPercent",
                    defaultMessage: "Weight",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(nodeData?.weightPercent, {
                    maximumFractionDigits: 0,
                    style: "percent",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.adjustedValue",
                    defaultMessage: "Adj. Value",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(nodeData?.adjustedValue, {
                    currency: localSettings.getCurrencyCode(),
                    minimumFractionDigits: 0,
                    style: "currency",
                  })}</td>
                </tr>
              </table>`;
            },

            pointFormatter: function (this: Point) {
              const { from, to } = this as SeriesSankeyPointOptionsObject;
              const pointerData: CustomData = (
                this.series.data as SeriesSankeyPointOptionsObject[]
              ).find((d) => d.from === from)?.custom as CustomData;

              return `<span"><b>${from} → ${to}</b></span><br/>
              <table style="margin-top: 8px;">
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.currentDeals",
                    defaultMessage: "Current # deals",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${pointerData?.currentDeals}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.allTimeDeals",
                    defaultMessage: "All-time # deals",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${pointerData?.allTimeDeals}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.percentDealsMadeStage",
                    defaultMessage: "% deals made stage",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(pointerData?.dealsPercent, {
                    maximumFractionDigits: 0,
                    style: "percent",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.totalValue",
                    defaultMessage: "Total value",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(pointerData?.totalValue, {
                    currency: localSettings.getCurrencyCode(),
                    minimumFractionDigits: 0,
                    style: "currency",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.weightPercent",
                    defaultMessage: "Weight",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(pointerData?.weightPercent, {
                    maximumFractionDigits: 0,
                    style: "percent",
                  })}</td>
                </tr>
                <tr>
                  <td>${intl.formatMessage({
                    id: "reports.deals.progressionChart.adjustedValue",
                    defaultMessage: "Adj. Value",
                    description: "Add button title on Company Associations modal",
                  })}</td>
                  <td align="right">${intl.formatNumber(pointerData?.adjustedValue, {
                    currency: localSettings.getCurrencyCode(),
                    minimumFractionDigits: 0,
                    style: "currency",
                  })}</td>
                </tr>
              </table>
              `;
            },
            useHTML: true,
          },
          type: "sankey",
        },
      ],
      subtitle: {
        align: "left",
        text: subtitle,
      },
      title: {
        align: "left",
        margin: 30,
        text: title,
      },
      tooltip: {
        useHTML: true,
      },
    };
  }, [funnelSummary, intl, onSelectStage, subtitle, title]);
};

export default useChartConfig;
